import { Component, signal, computed, ChangeDetectionStrategy, inject, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterLink, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidenavComponent } from '../sidenav/sidenav.component';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { ProgressBarModule } from 'primeng/progressbar';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { PopoverModule } from 'primeng/popover';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { AccountInfo } from '@azure/msal-browser';
import { AuthService } from '@core/services/auth.service';
import { PreferencesService } from '@core/services/preferences.service';
import { VersionService } from '@core/services/version.service';
import { AccessService } from '@core/services/access.service';
import { DocumentViewerModalComponent } from '@features/search/components/document-viewer-modal/document-viewer-modal.component';
import { VersionMismatchModalComponent } from '@shared/components/version-mismatch-modal/version-mismatch-modal.component';
import { EllaIssueNotificationService } from '@features/ella/services/ella-issue-notification.service';
import { SearchResult } from '@core/models/search.model';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    SidenavComponent,
    ButtonModule,
    AvatarModule,
    MenuModule,
    MenubarModule,
    DocumentViewerModalComponent,
    VersionMismatchModalComponent,
    ProgressBarModule,
    RippleModule,
    ToastModule,
    OverlayBadgeModule,
    PopoverModule,
    TagModule,
    TooltipModule
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'onResize()'
  }
})
export class MainLayoutComponent implements OnDestroy {
  authService = inject(AuthService);
  preferences = inject(PreferencesService);
  accessService = inject(AccessService);
  issueNotifications = inject(EllaIssueNotificationService);
  private router = inject(Router);

  // Signals for responsive behavior
  sidenavVisible = signal(true);
  isMobile = signal(false);

  private activityTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;
  private readonly onActivity = () => this.startActivityTimer();

  // Navigation loading indicator
  isNavigating = signal(false);

  readonly issueBadgeValue = computed(() => {
    const count = this.issueNotifications.activeCount();
    if (count <= 0) return undefined;
    return count > 9 ? '9+' : String(count);
  });

  // Document viewer modal
  showDocumentModal = signal(false);
  selectedDocumentResult = signal<SearchResult | null>(null);

  // MenuBar items (empty for now, can be populated with top-level navigation)
  menuItems: MenuItem[] = [];

  readonly userMenuItems = computed<MenuItem[]>(() => {
    this.authService.currentUser(); // reactive dependency — recomputes when account switches
    const otherAccounts = this.authService.getOtherAccounts();

    const accountItems: MenuItem[] = otherAccounts.map(account => ({
      label: account.name || account.username,
      data: {
        type: 'account',
        account,
        email: account.username,
        initials: (account.name || account.username)
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      },
      command: () => this.switchAccount(account)
    }));

    const baseItems: MenuItem[] = [
      { label: 'Settings', icon: 'pi pi-cog', command: () => this.router.navigate(['/settings']) },
      { separator: true },
      { label: 'Logout', icon: 'pi pi-sign-out', command: () => this.logout() }
    ];

    return accountItems.length > 0
      ? [...accountItems, { separator: true }, ...baseItems]
      : baseItems;
  });

  constructor() {
    this.checkScreenSize();
    this.preferences.initTheme();

    effect(() => this.startActivityTimer());
    this.ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, this.onActivity, { passive: true }));

    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isNavigating.set(true);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.isNavigating.set(false);
      }
    });
  }

  // Computed values using getters to avoid initialization issues
  get currentUser() {
    return this.authService.currentUser();  // Call the signal to get the value
  }

  get userInitials() {
    const user = this.currentUser;  // Now this is the value, not a signal
    if (!user) return '';
    return user.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  }

  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    const width = window.innerWidth;
    const isMobile = width < 768;

    this.isMobile.set(isMobile);

    // On mobile, sidenav should be closed by default
    if (isMobile) {
      this.sidenavVisible.set(false);
    } else {
      this.sidenavVisible.set(true);
    }
  }

  toggleSidenav(): void {
    this.sidenavVisible.set(!this.sidenavVisible());
  }

  closeSidenavOnMobile(): void {
    if (this.isMobile()) {
      this.sidenavVisible.set(false);
    }
  }

  navigateToProfile(): void {
    console.error('navigateToProfile not implemented yet.');
  }

  logout(): void {
    this.authService.logout();
  }

  getUserName(): string {
    return this.currentUser?.name || '';
  }

  getUserEmail(): string {
    return this.currentUser?.email || '';
  }

  getUserInitials(): string {
    return this.userInitials;
  }

  switchAccount(account: AccountInfo): void {
    this.authService.switchAccount(account);
  }

  issueSeverity(severity?: string): 'danger' | 'warn' {
    return severity === 'Critical' ? 'danger' : 'warn';
  }

  dismissIssue(id?: number): void {
    if (id == null) return;
    this.issueNotifications.dismiss(id);
  }

  goToIssues(): void {
    this.router.navigate(['/ella', 'health', 'issues']);
  }

  onSidenavNavigate(): void {
    this.closeSidenavOnMobile();
  }

  /**
   * Handle document modal display from search
   */
  onShowDocumentModal(result: SearchResult): void {
    this.selectedDocumentResult.set(result);
    this.showDocumentModal.set(true);
  }

  /**
   * Handle document modal close
   */
  onDocumentModalClose(): void {
    this.showDocumentModal.set(false);
    this.selectedDocumentResult.set(null);
  }

  // Top nav items (can be populated if needed)
  topNavItems: MenuItem[] = [];

  ngOnDestroy(): void {
    this.clearActivityTimer();
    this.ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, this.onActivity));
  }

  private startActivityTimer(): void {
    this.clearActivityTimer();
    const val = this.preferences.sessionTimeout();
    if (val === 'never') return;
    const ms = parseInt(val, 10) * 60 * 1000;
    this.activityTimer = setTimeout(() => this.logout(), ms);
  }

  private clearActivityTimer(): void {
    if (this.activityTimer !== null) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
  }
}
